/**
 * Public Challenge Leaderboard — /leaderboard
 *
 * Shows players ranked by challenge performance (wins, win rate, earnings).
 * Matches the GamePoint navy/neon design language.
 */
import { useState, useMemo } from "react";
import { Link } from "wouter";
import { trpc } from "@/lib/trpc";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Trophy,
  Medal,
  Flame,
  TrendingUp,
  Users,
  Swords,
  DollarSign,
  ChevronDown,
  Crown,
  Zap,
  ArrowUpDown,
} from "lucide-react";

type SortBy = "wins" | "winRate" | "earnings" | "challenges";

const sortOptions: { value: SortBy; label: string; icon: React.ElementType }[] = [
  { value: "wins", label: "Most Wins", icon: Trophy },
  { value: "winRate", label: "Win Rate", icon: TrendingUp },
  { value: "earnings", label: "Top Earners", icon: DollarSign },
  { value: "challenges", label: "Most Active", icon: Swords },
];

function getRankBadge(rank: number) {
  if (rank === 1) return <Crown className="w-5 h-5 text-yellow-400" />;
  if (rank === 2) return <Medal className="w-5 h-5 text-slate-300" />;
  if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
  return <span className="text-slate-500 font-mono text-sm">#{rank}</span>;
}

function getRankBg(rank: number) {
  if (rank === 1) return "bg-yellow-500/10 border-yellow-500/30";
  if (rank === 2) return "bg-slate-300/5 border-slate-400/20";
  if (rank === 3) return "bg-amber-600/5 border-amber-600/20";
  return "bg-white/[0.02] border-white/5";
}

export default function Leaderboard() {
  const [sortBy, setSortBy] = useState<SortBy>("wins");
  const [showSortMenu, setShowSortMenu] = useState(false);

  const { data, isLoading } = trpc.leaderboard.getLeaderboard.useQuery(
    { sortBy, limit: 50, offset: 0 },
    { placeholderData: (prev) => prev }
  );

  const { data: stats } = trpc.leaderboard.stats.useQuery();

  const currentSortOption = useMemo(
    () => sortOptions.find((o) => o.value === sortBy) ?? sortOptions[0],
    [sortBy]
  );

  return (
    <div className="min-h-screen bg-[#0a1628] text-white">
      <PublicNav />

      {/* Hero Section */}
      <section className="relative pt-28 pb-16 overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-neon/5 rounded-full blur-[120px]" />
        </div>

        <div className="max-w-6xl mx-auto px-4 relative z-10">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-neon/10 border border-neon/20 rounded-full px-4 py-1.5 mb-6">
              <Swords className="w-4 h-4 text-neon" />
              <span className="text-neon text-xs font-semibold uppercase tracking-wider">Challenge Leaderboard</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight mb-4">
              TOP <span className="text-neon">CHALLENGERS</span>
            </h1>
            <p className="text-slate-400 max-w-xl mx-auto">
              The best players on GamePoint, ranked by head-to-head challenge performance. Think you can compete? Challenge someone today.
            </p>
          </div>

          {/* Stats Bar */}
          {stats && (
            <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto mb-10">
              <div className="text-center">
                <div className="text-2xl font-black text-neon">{stats.totalPlayers}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">Players</div>
              </div>
              <div className="text-center border-x border-white/10">
                <div className="text-2xl font-black text-neon">{stats.totalChallenges}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">Challenges</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-black text-neon">
                  ${(stats.totalPrizePool || 0) / 100 > 0
                    ? (stats.totalPrizePool / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    : "—"}
                </div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">Challenge Prizes</div>
              </div>
            </div>
          )}

          {/* Sort Controls */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold uppercase tracking-wide text-slate-300">
              Rankings
            </h2>
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSortMenu(!showSortMenu)}
                className="bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white gap-2"
              >
                <ArrowUpDown className="w-3.5 h-3.5" />
                {currentSortOption.label}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showSortMenu ? "rotate-180" : ""}`} />
              </Button>
              {showSortMenu && (
                <div className="absolute right-0 mt-1 w-48 bg-[#141828] border border-white/10 rounded-lg shadow-xl z-20 overflow-hidden">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSortBy(option.value);
                        setShowSortMenu(false);
                      }}
                      className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${
                        sortBy === option.value
                          ? "bg-neon/10 text-neon"
                          : "text-slate-400 hover:bg-white/5 hover:text-white"
                      }`}
                    >
                      <option.icon className="w-4 h-4" />
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Leaderboard Table */}
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="h-16 bg-white/[0.02] border border-white/5 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : !data?.players?.length ? (
            <div className="text-center py-20">
              <Swords className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-slate-400 mb-2">No Challenges Yet</h3>
              <p className="text-slate-500 text-sm mb-6">
                Be the first to challenge another player and claim the top spot!
              </p>
              <Link href="/console/challenges">
                <Button className="bg-neon hover:bg-neon/90 text-black font-semibold">
                  Send a Challenge
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Header row */}
              <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-2 text-xs text-slate-500 uppercase tracking-wider font-semibold">
                <div className="col-span-1 text-center">Rank</div>
                <div className="col-span-4">Player</div>
                <div className="col-span-2 text-center">Record</div>
                <div className="col-span-2 text-center">Win Rate</div>
                <div className="col-span-1 text-center">Streak</div>
                <div className="col-span-2 text-right">Earnings</div>
              </div>

              {data.players.map((player, index) => {
                const rank = index + 1;
                return (
                  <div
                    key={player.userId}
                    className={`grid grid-cols-12 gap-4 items-center px-4 py-3 rounded-xl border transition-colors hover:bg-white/[0.04] ${getRankBg(rank)}`}
                  >
                    {/* Rank */}
                    <div className="col-span-2 md:col-span-1 flex items-center justify-center">
                      {getRankBadge(rank)}
                    </div>

                    {/* Player Info */}
                    <div className="col-span-10 md:col-span-4 flex items-center gap-3">
                      <Avatar className="h-9 w-9 border border-white/10 shrink-0">
                        <AvatarFallback className="text-xs font-bold bg-neon/20 text-neon">
                          {player.name?.charAt(0).toUpperCase() || "?"}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="font-semibold text-white text-sm truncate">{player.name}</div>
                        <div className="text-xs text-slate-500 capitalize">{player.platformRole || "Player"}</div>
                      </div>
                    </div>

                    {/* Record (W-L) */}
                    <div className="col-span-4 md:col-span-2 text-center">
                      <span className="text-neon font-bold">{player.wins}</span>
                      <span className="text-slate-600 mx-1">-</span>
                      <span className="text-red-400 font-bold">{player.losses}</span>
                      <div className="text-[10px] text-slate-500 md:hidden">Record</div>
                    </div>

                    {/* Win Rate */}
                    <div className="col-span-4 md:col-span-2 text-center">
                      <div className="inline-flex items-center gap-1">
                        <TrendingUp className="w-3.5 h-3.5 text-neon/60" />
                        <span className={`font-bold text-sm ${player.winRate >= 60 ? "text-neon" : player.winRate >= 40 ? "text-yellow-400" : "text-slate-400"}`}>
                          {player.winRate}%
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-500 md:hidden">Win Rate</div>
                    </div>

                    {/* Streak */}
                    <div className="col-span-2 md:col-span-1 text-center">
                      {player.streak > 0 ? (
                        <div className="inline-flex items-center gap-0.5 text-orange-400">
                          <Flame className="w-3.5 h-3.5" />
                          <span className="font-bold text-sm">{player.streak}</span>
                        </div>
                      ) : (
                        <span className="text-slate-600 text-sm">—</span>
                      )}
                    </div>

                    {/* Earnings */}
                    <div className="col-span-2 md:col-span-2 text-right">
                      {player.earningsPrivate ? (
                        <span className="text-slate-600 text-xs italic">Private</span>
                      ) : (
                        <span className="text-neon font-bold text-sm">
                          ${(player.totalEarnings / 100).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* CTA Section */}
          <div className="mt-16 text-center">
            <div className="inline-block bg-gradient-to-r from-neon/10 via-transparent to-neon/10 border border-neon/20 rounded-2xl p-8 max-w-md">
              <Zap className="w-8 h-8 text-neon mx-auto mb-3" />
              <h3 className="text-xl font-black uppercase mb-2">Ready to Compete?</h3>
              <p className="text-slate-400 text-sm mb-5">
                Challenge any player to a head-to-head match and climb the leaderboard.
              </p>
              <Link href="/console/challenges">
                <Button className="bg-neon hover:bg-neon/90 text-black font-bold px-8">
                  Start Challenging
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
